import { ErrorCode, ErrorCodeRuntype, parseEndpoint } from '@luminovo/api-contracts';
import * as Sentry from '@sentry/core';

import { logToExternalErrorHandlers } from './logToExternalErrorHandlers';

/** Sentry drops tag values longer than 200 characters. */
const MAX_RECEIVED_CODE_LENGTH = 200;

/** Reports a code the frontend doesn't know and returns 'unknown'; `receivedCode` is the backend's raw string. */
export async function validateErrorCode(
    response: Response,
    endpoint: string,
): Promise<{ code: ErrorCode; receivedCode: string }> {
    // The body stream can only be read once, so we parse it once and reuse it below.
    const body = await response.json().catch(() => undefined);
    const uncheckedCode = body && typeof body.code === 'string' ? body.code : 'unknown';

    const parsed = ErrorCodeRuntype.safeParse(uncheckedCode);
    if (parsed.success) {
        return { code: parsed.data, receivedCode: parsed.data };
    }

    const { method } = parseEndpoint(endpoint);
    const { status } = response;
    const receivedCode = uncheckedCode.slice(0, MAX_RECEIVED_CODE_LENGTH);

    // Logged on its own, before anything truncates it: the only verbatim record of a code the frontend doesn't know.
    Sentry.logger.error(
        Sentry.logger
            .fmt`http.unknown_error_code endpoint=${endpoint} status=${status} method=${method} received_code=${receivedCode}`,
        { endpoint, method, status, received_code: receivedCode },
    );

    // Not the full enum: its hundreds of entries would push the received code past Sentry's 8192-character limit.
    const ourError = new Error(
        `Unknown error code "${receivedCode}" received from ${endpoint} with status ${status}. Expected one of the ${ErrorCodeRuntype.options.length} codes in packages/shared/api-contracts/src/resources/errorCodes.ts.`,
    );
    // Throwing a runtype error is not necessary because the failed request will always throw an HttpError.
    logToExternalErrorHandlers(ourError, {
        tags: { received_error_code: receivedCode },
        extra: { endpoint, method, status, receivedCode },
        // By code, so each new backend code gets its own issue rather than hiding in this endpoint's first one.
        fingerprint: ['RUNTYPE_ERROR', endpoint, receivedCode],
    });
    return { code: 'unknown', receivedCode };
}
